import { useEffect, useRef } from 'react';
import { P2GBox, P2GCard, P2GContainer, P2GIcon, P2GText, SvgTickCircle } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { tierLabel } from '~/apps/subscriptions/types';
import { SubscribeOutcome } from '~/apps/subscriptions/subscribe-panel';
import { OnboardingScreenContentWrapper } from '../../OnboardingScreenContentWrapper';

/** Purely informational - nothing for the customer to act on - so the wizard moves on by itself
 * rather than waiting for a press. */
const AUTO_ADVANCE_MS = 3000;

type props = {
  outcome: SubscribeOutcome;
  onDone: () => void;
};

const SubscribeSuccessScreen = ({ outcome, onDone }: props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const tierName = outcome.tier ? tierLabel(outcome.tier) : null;

  // Read through a ref so the timer is set once per mount, not reset by a parent re-render handing
  // down a fresh inline callback (AGENTS.md's effect-dependency rule).
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    const timer = setTimeout(() => onDoneRef.current(), AUTO_ADVANCE_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    // Same wrapper + `P2GContainer maxWidth="md"` cap every sibling onboarding screen uses
    // (intro/complete/etc.) - a bespoke full-bleed box here left the card looking lost in
    // whatever width the host happens to be, rather than the wizard's own content column.
    <OnboardingScreenContentWrapper>
      <P2GContainer maxWidth="md">
        <P2GBox display="flex" justifyContent="center" sx={{ mt: 8 }}>
          {/* Matches the width `SubscriptionCheckoutFrame` itself renders at here (this same
              `md` container, 960px) - the card replaces that iframe, so it takes its width too. */}
          <P2GCard variant="outlined" sx={{ textAlign: 'center', p: 4, width: '100%' }}>
            <P2GIcon size="lg" palette="success" aria-hidden>
              <SvgTickCircle />
            </P2GIcon>
            {/* P2GText renders as `<span>` by default - block siblings need `component` set
                explicitly, or they sit on the same line with nothing forcing a break (same fix
                as plan-widget.tsx). */}
            <P2GText component="p" variant="h6" sx={{ mt: 2 }}>
              {tierName
                ? t('SETUP_SUBSCRIBE_SUCCESS_TITLE_TIER', { defaultValue: "You're on {{tier}}", tier: tierName })
                : t('SETUP_SUBSCRIBE_SUCCESS_TITLE', { defaultValue: 'Subscription active' })}
            </P2GText>
            <P2GText component="p" variant="body2" sx={{ mt: 1 }}>
              {t('SETUP_SUBSCRIBE_SUCCESS_SUBTITLE', { defaultValue: 'Your subscription is active.' })}
            </P2GText>
          </P2GCard>
        </P2GBox>
      </P2GContainer>
    </OnboardingScreenContentWrapper>
  );
};

export default SubscribeSuccessScreen;
